import { invoiceBillingAddressLines, type InvoiceBillingAddress } from "../../shared/invoice-address";
import { countries } from "./countries";
import { type DocumentBusinessDetails, emptyBusinessAddress } from "../../shared/document-business-details";

const domesticCountries = new Set(["us", "usa", "unitedstates", "unitedstatesofamerica"]);
const postalRegions: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA", colorado: "CO",
  connecticut: "CT", delaware: "DE", "district of columbia": "DC", florida: "FL", georgia: "GA",
  hawaii: "HI", idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA", kansas: "KS",
  kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD", massachusetts: "MA",
  michigan: "MI", minnesota: "MN", mississippi: "MS", missouri: "MO", montana: "MT",
  nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ", "new mexico": "NM",
  "new york": "NY", "north carolina": "NC", "north dakota": "ND", ohio: "OH", oklahoma: "OK",
  oregon: "OR", pennsylvania: "PA", "rhode island": "RI", "south carolina": "SC",
  "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT", vermont: "VT",
  virginia: "VA", washington: "WA", "west virginia": "WV", wisconsin: "WI", wyoming: "WY",
  "american samoa": "AS", guam: "GU", "northern mariana islands": "MP", "puerto rico": "PR",
  "us virgin islands": "VI", "u.s. virgin islands": "VI",
};

function postalRegionLine(line: string): string {
  return line.replace(/,\s*([A-Za-z][A-Za-z .]*?)\s+(\d{5}(?:-\d{4})?)$/, (match, region: string, postalCode: string) => {
    const abbreviation = postalRegions[region.trim().toLowerCase()];
    return abbreviation ? `, ${abbreviation} ${postalCode}` : match;
  });
}

export function documentAddressLines(address: string | InvoiceBillingAddress): string[] {
  const lines = typeof address === "string"
    ? address.split(/\r\n?|\n/).map(line => line.trim()).filter(Boolean)
    : invoiceBillingAddressLines(address);
  const countryLine = (line: string) => domesticCountries.has(line.toLowerCase().replace(/[^a-z]/g, ""))
    || countries.some(country => country.name.toLowerCase() === line.toLowerCase() || country.code === line.toUpperCase());
  const country = typeof address === "string" ? lines.find(countryLine) : address.country;
  const domestic = Boolean(country && domesticCountries.has(country.toLowerCase().replace(/[^a-z]/g, "")));
  const visible = typeof address === "string" ? lines.filter(line => !countryLine(line))
    : invoiceBillingAddressLines({ ...address, country: null });
  if (country && !domestic) return visible;
  return visible.map(postalRegionLine);
}

// Format invoice addresses before appending phone so a country is never hidden
// in the middle of a combined contact string. Stored address fields are untouched.
export function documentIssuerContactLines(details: DocumentBusinessDetails): string[] {
  return [
    ...(details.showAddress !== false ? documentAddressLines(details.address) : []),
    ...(details.showPhone && details.phone ? [details.phone] : []),
  ];
}

// Historical issuer snapshots retain their original strings. Only the editable
// copy is decoded; unknown address parts remain visible for manual correction.
export function reviewBusinessAddressForEditing(address: string | InvoiceBillingAddress): { address: InvoiceBillingAddress; contactLines: string[] } {
  if (typeof address !== "string") return { address: { ...address }, contactLines: [] };
  const lines = address.split(/\r\n?|\n/).map(line => line.trim()).filter(Boolean);
  const fields = emptyBusinessAddress();
  fields.line1 = lines[0] ?? null;
  const remainder: string[] = [], contactLines: string[] = [];
  for (const line of lines.slice(1)) {
    // Do not guess from arbitrary numeric apartment/unit identifiers. Only labeled
    // contact information, email addresses, or recognizably formatted phones need review.
    if (/^(?:phone|tel(?:ephone)?|mobile|fax|e-?mail)\s*:/i.test(line)
      || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(line)
      || /^\+\d{1,3}(?:[ .()-]+\d+){2,}$/.test(line)
      || /^(?:\+1[ .-]?)?(?:\([2-9]\d{2}\)[ .-]?|[2-9]\d{2}[ .-])[2-9]\d{2}[ .-]\d{4}(?:\s*(?:ext\.?|x)\s*\d+)?$/i.test(line)) {
      contactLines.push(line); continue;
    }
    const country = countries.find(country => country.name.toLowerCase() === line.toLowerCase() || country.code === line.toUpperCase());
    if (country || domesticCountries.has(line.toLowerCase().replace(/[^a-z]/g, ""))) {
      fields.country = country?.code ?? "US";
      continue;
    }
    const location = line.match(/^(.+),\s*([A-Za-z][A-Za-z .]*?)\s+(\d{5}(?:-\d{4})?)$/);
    const region = location && (postalRegions[location[2]!.trim().toLowerCase()] ?? location[2]!.trim().toUpperCase());
    if (location && !fields.locality && region && Object.values(postalRegions).includes(region)) {
      fields.country ??= "US";
      fields.locality = location[1]!.trim();
      fields.region = region;
      fields.postalCode = location[3]!;
    } else remainder.push(line);
  }
  fields.line2 = remainder.length ? remainder.join(", ") : null;
  return { address: fields, contactLines };
}

export function businessAddressForEditing(address: string | InvoiceBillingAddress): InvoiceBillingAddress {
  const review = reviewBusinessAddressForEditing(address);
  if (review.contactLines.length) throw new Error("Review the saved legacy contact details before editing this address.");
  return review.address;
}
